import { useMemo } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Code,
  Flex,
  Icon,
  IconButton,
  ListItem,
  Text,
  UnorderedList,
  useToast,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';

interface Props {
  error: string | null;
  title?: string; // Optional custom title for the alert header
  introKey?: string; // i18n key override for intro text
  listTitleKey?: string; // i18n key override for list title
}

const AnvaQuoteInlineError = ({
  error,
  title,
  introKey,
  listTitleKey,
}: Props) => {
  const { t } = useTranslation();
  const toast = useToast();

  const showGenericMessage = shouldShowGenericMessage(error);

  const errorLines = useMemo(() => extractAnvaErrors(error), [error]);
  const copyText = useMemo(() => errorLines.join('\n'), [errorLines]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(copyText || (error ?? ''));
      toast({
        title: t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD_SUCCESS'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD_FAILED'),
        description: t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD_MANUAL-INSTRUCTION'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Show generic blue message for specific error conditions
  if (showGenericMessage) {
    return (
      <Alert status="info">
        <AlertDescription>
          {t('FLOWS_PRODUCTS_QUOTE_ANVA-ERROR-GENERIC-MESSAGE')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing="3" w="full">
      <Box>
        <Alert status="error">
          <VStack align="stretch" spacing={1} flex={1}>
            {title && <AlertTitle>{title}</AlertTitle>}
            <AlertDescription>
              {t(introKey || 'FLOWS_PRODUCTS_QUOTE_ANVA-ERROR-CONTACT')}
            </AlertDescription>
          </VStack>
        </Alert>
      </Box>
      <Flex justify="space-between" align="center">
        <Text fontWeight="semibold" textStyle="smallBody">
          {t(listTitleKey || 'FLOWS_PRODUCTS_QUOTE_ANVA-ERROR-LIST-TITLE')}:
        </Text>
        <IconButton
          aria-label={t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD')}
          size="xs"
          icon={<Icon name="GeneralActionsDuplicate" boxSize="20px" />}
          onClick={copyToClipboard}
          variant="ghost"
        />
      </Flex>

      {errorLines.length > 0 ? (
        <UnorderedList pl="5" spacing={1} stylePosition="outside">
          {errorLines.map((line, idx) => (
            <ListItem key={`${line}-${idx}`}>
              <Text fontSize="sm">{line}</Text>
            </ListItem>
          ))}
        </UnorderedList>
      ) : (
        <Box
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Code p={3} whiteSpace="pre-wrap" width="100%" display="block">
            {error || ''}
          </Code>
        </Box>
      )}
    </VStack>
  );
};

export default AnvaQuoteInlineError;

// Check if error should show generic blue message instead of detailed red error
const shouldShowGenericMessage = (errorString: string | null): boolean => {
  if (!errorString) return false;

  const hasBerichtstatus11 = errorString.includes('berichtstatus : 11');
  const hasMinus9999 = errorString.includes('-9999');
  const hasBOAF = errorString.includes('BOAF');
  const wgErrorMessage = errorString.includes(
    'Deze aanvraag kan niet worden berekend op basis van de ingevoerde waarden. Vraag maatwerk offerte aan.',
  );

  // Show generic message if:
  // - Error includes "berichtstatus : 11" OR
  // - Error includes "-9999" AND does NOT include "BOAF"
  // - error is generated by WG
  return wgErrorMessage || hasBerichtstatus11 || (hasMinus9999 && !hasBOAF);
};

// Try to extract a readable list of error lines from various error payload shapes
const extractAnvaErrors = (errorString: string | null): string[] => {
  if (!errorString) return [];

  const collectFromUnknown = (value: unknown): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            // Common shapes: { message }, { description }, { detail }
            const obj = item as Record<string, unknown>;
            return (
              (obj.message as string) ||
              (obj.description as string) ||
              (obj.detail as string) ||
              ''
            );
          }
          return '';
        })
        .filter((s) => s && s.trim().length > 0) as string[];
    }
    if (typeof value === 'string') return value.split('\n');
    return [];
  };

  // Attempt to parse JSON first
  try {
    const parsedUnknown: unknown = JSON.parse(errorString);
    const asRecord = (v: unknown): Record<string, unknown> | null =>
      v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
    const parsed = asRecord(parsedUnknown);
    if (!parsed) throw new Error('not-an-object');
    // Direct errors array
    const direct = collectFromUnknown(parsed['errors']);
    if (direct.length) return direct;

    // Nested arrays we have seen in some payloads
    const dataObj = asRecord(parsed['data']);
    const nestedData = collectFromUnknown(dataObj?.['errors']);
    if (nestedData.length) return nestedData;

    // If message contains JSON again, recurse
    const messageValue = parsed['message'];
    if (typeof messageValue === 'string') {
      try {
        return extractAnvaErrors(messageValue);
      } catch {
        // fall through to text splitting below
      }
    }

    // Fallback: stringify object and split by newlines/bullets
    return (JSON.stringify(parsed) || '')
      .split(/\n|•|-\s/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  } catch {
    // Not JSON; split by lines and common bullet prefixes
    return errorString
      .split(/\n|•|-\s/) // handle dashes and bullets
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
};
